import type { ProductType } from '@/features/product/types';
import {
  US_SUBMISSION_PATHWAYS,
  type SubmissionGroup,
  type SubmissionOption,
} from '@/features/dossier/us-submission-pathways';
import { CA_SUBMISSION_PATHWAYS } from '@/features/dossier/ca-submission-pathways';
import { EU_SUBMISSION_PATHWAYS } from '@/features/dossier/eu-submission-pathways';
import { AU_SUBMISSION_PATHWAYS } from '@/features/dossier/au-submission-pathways';
import { AU_IVD_SUBMISSION_PATHWAYS } from '@/features/dossier/au-ivd-submission-pathways';
import { UK_SUBMISSION_PATHWAYS } from '@/features/dossier/uk-submission-pathways';
import { NI_SUBMISSION_PATHWAYS } from '@/features/dossier/ni-submission-pathways';

/**
 * Submission pathways per enabled country — reuses the dossier's per-country
 * data (one source of truth) so the product-creation Step-2 picker offers
 * exactly what the dossier does. Keyed by the `/api/countries` code.
 */
const PATHWAYS_BY_COUNTRY: Record<string, SubmissionGroup[]> = {
  US: US_SUBMISSION_PATHWAYS,
  EU: EU_SUBMISSION_PATHWAYS,
  CA: CA_SUBMISSION_PATHWAYS,
  UK: UK_SUBMISSION_PATHWAYS,
  // AU keeps IVD classes in a second file; dossier shows both, so we do too.
  AU: [...AU_SUBMISSION_PATHWAYS, ...AU_IVD_SUBMISSION_PATHWAYS],
};

interface SubMarketDef {
  /** The classification-map key this sub-market stores under. */
  key: string;
  /** The heading shown above this sub-market's dropdown. */
  label: string;
  source: SubmissionGroup[];
}

/**
 * Countries whose regulatory classification splits into named sub-markets — the
 * user classifies each one. Mirrors the dossier's UK dispatch, which routes
 * Great Britain (UKCA) and Northern Ireland (CE / Windsor Framework)
 * separately. Each sub-market persists under its own composite key ('UK-GB' /
 * 'UK-NI') in the same flat `{key: classification}` map, so no schema change.
 */
const SUB_MARKETS: Record<string, SubMarketDef[]> = {
  UK: [
    {
      key: 'UK-GB',
      label: 'Great Britain — Regulatory Classification',
      source: UK_SUBMISSION_PATHWAYS,
    },
    {
      key: 'UK-NI',
      label: 'Northern Ireland — Regulatory Classification',
      source: NI_SUBMISSION_PATHWAYS,
    },
  ],
};

/**
 * Step 2 asks for a product's regulatory CLASSIFICATION, not a full submission
 * pathway — so we show just the class name and drop the dossier labels' framework
 * citations / process descriptions (everything after the em-dash), e.g.
 * "Class IIa — TGA conformity assessment (Essential Principles)" → "Class IIa".
 * This ONLY affects Step 2; the shared dossier files (RA-reviewed verbatim) are
 * left untouched. A few US values carry their distinguishing detail after the
 * dash, so they're overridden explicitly to avoid ambiguity / collisions.
 */
const OPTION_LABEL_OVERRIDES: Record<string, string> = {
  '510k': '510(k) Traditional',
  // Both De Novo variants (with / without clinical data) are a submission-time
  // detail, not a classification — collapse them to one "De Novo" option.
  'denovo-no-clinical': 'De Novo',
  'denovo-clinical': 'De Novo',
};

const shortLabel = (label: string): string => label.split(' — ')[0]!.trim();

function simplifyGroups(groups: SubmissionGroup[]): SubmissionGroup[] {
  return groups.map((g) => {
    const seen = new Set<string>();
    const options: SubmissionOption[] = [];
    for (const o of g.options) {
      const label = OPTION_LABEL_OVERRIDES[o.value] ?? shortLabel(o.label);
      if (seen.has(label)) continue; // collapse duplicates (e.g. the two De Novos)
      seen.add(label);
      options.push({ ...o, label });
    }
    return { ...g, label: shortLabel(g.label), options };
  });
}

/**
 * EU bundles MDR + IVDR device classes in one dossier group; Step 2 splits them
 * into separately-headed groups so IVDR gets its own label (like AU / UK). This
 * targets EU only — it's the sole country with both `mdr-` and `ivdr-` prefixed
 * options in a single group.
 */
function splitMdrIvdr(groups: SubmissionGroup[]): SubmissionGroup[] {
  return groups.flatMap((g) => {
    const mdr = g.options.filter((o) => o.value.startsWith('mdr-'));
    const ivdr = g.options.filter((o) => o.value.startsWith('ivdr-'));
    if (mdr.length > 0 && ivdr.length > 0) {
      return [
        { ...g, label: 'Medical Device (MDR)', options: mdr },
        { ...g, label: 'In-Vitro Diagnostic (IVDR)', options: ivdr },
      ];
    }
    return [g];
  });
}

export interface ResolvedMarket {
  /** Classification-map key (a country code, or 'UK-GB' / 'UK-NI'). */
  key: string;
  /** Sub-market heading, or null for a single-market country. */
  label: string | null;
  /** Grouped options, each rendered under its own `SelectLabel`. */
  groups: SubmissionGroup[];
}

/**
 * The sub-markets to render for a selected country, kept AS GROUPS so each
 * dropdown shows its `SelectLabel` headers (e.g. "Medical Device" vs
 * "In-Vitro Diagnostic", or EU's combined "Medical Device — MDR / IVDR"). Most
 * countries return one market (label null); UK returns Great Britain + Northern
 * Ireland. Combination products use the device set (mirrors dossier dispatch).
 */
export function marketsFor(
  country: string,
  productType: ProductType,
): ResolvedMarket[] {
  const pt = productType === 'combination' ? 'device' : productType;
  const prepare = (gs: SubmissionGroup[]) =>
    simplifyGroups(splitMdrIvdr(gs.filter((g) => g.productType === pt)));
  const subs = SUB_MARKETS[country];
  if (subs) {
    return subs.map((s) => ({
      key: s.key,
      label: s.label,
      groups: prepare(s.source),
    }));
  }
  return [{ key: country, label: null, groups: prepare(PATHWAYS_BY_COUNTRY[country] ?? []) }];
}

/** The classification-map keys a country contributes (its sub-markets). */
export function marketKeysForCountry(country: string): string[] {
  const subs = SUB_MARKETS[country];
  return subs ? subs.map((s) => s.key) : [country];
}

/** Which country a classification-map key belongs to ('UK-GB' → 'UK'). */
export function countryOfMarketKey(key: string): string {
  for (const [country, subs] of Object.entries(SUB_MARKETS)) {
    if (subs.some((s) => s.key === key)) return country;
  }
  return key;
}

/** The distinct countries selected, derived from the classification-map keys. */
export function selectedCountries(map: Record<string, string>): string[] {
  const out: string[] = [];
  for (const key of Object.keys(map)) {
    const c = countryOfMarketKey(key);
    if (!out.includes(c)) out.push(c);
  }
  return out;
}

/**
 * A country is "ready" once at least one of its sub-markets is classified — so
 * a UK product sold only in Great Britain (GB set, NI left blank) still counts.
 */
export function isCountryReady(
  map: Record<string, string>,
  country: string,
): boolean {
  return marketKeysForCountry(country).some((k) => Boolean(map[k]));
}

/**
 * What the country tab SHOWS — finer than `isCountryReady`, which stays the gate: 'partial' is a
 * multi-market country (UK) with some but not all sub-markets classified. It is still ready (a
 * GB-only product can be created); the tab just doesn't claim NI is done.
 */
export function countryProgress(
  map: Record<string, string>,
  country: string,
): 'done' | 'partial' | 'none' {
  const keys = marketKeysForCountry(country);
  const set = keys.filter((k) => Boolean(map[k])).length;
  if (set === 0) return 'none';
  return set === keys.length ? 'done' : 'partial';
}

/**
 * Sentinel classification for "I don't know my pathway yet" — lets the user
 * proceed without committing to a class and points them at the Regulatory
 * Strategy agent, which recommends one. Stored like any other value; it is not
 * a finder pathway, so a product marked unsure stays out of the Predicate
 * Finder until it's actually classified.
 */
export const NOT_SURE = 'unsure';

/**
 * US-device classification for a 510(k)-EXEMPT device (most Class I, some Class
 * II) — no premarket submission and no predicate. User-declared in Step 2 (we
 * don't assert exemption). Not a dossier submission type, so it lives here as a
 * Step-2 option only; it is not a finder pathway, so an exempt device is
 * excluded from the Predicate Finder.
 */
export const US_510K_EXEMPT = '510k-exempt';

/**
 * The US classifications that qualify a device for the Predicate Finder —
 * mirrors the backend gate (backend/services/finder_eligibility, derived from
 * requires_predicate_run): Traditional + Abbreviated 510(k) only.
 */
export const FINDER_US_PATHWAYS: readonly string[] = ['510k', '510k-abbreviated'];

/**
 * The EU classifications that qualify a device for the Existing Devices finder
 * — mirrors the backend gate
 * (backend/services/finder_eligibility._FINDER_EU_PATHWAYS).
 *
 * MDR classes route to the Art 61 clinical-comparator search. The wizard never
 * offers self-declared MDR Class I, so every MDR option already implies
 * Notified Body involvement.
 *
 * IVDR classes C and D route to a DIFFERENT agent — the state-of-the-art
 * benchmark finder (Reg 2017/746 has no equivalence route at all). Classes A,
 * A-sterile and B stay excluded: no Summary of Safety and Performance, no
 * common specifications and no NB technical-documentation assessment, so the
 * finder would have nothing to work from.
 */
export const FINDER_EU_PATHWAYS: readonly string[] = [
  'mdr-class-iia',
  'mdr-class-iib',
  'mdr-class-iii',
  'ivdr-class-c',
  'ivdr-class-d',
];

/** Which markets a device qualifies for individually, so guidance can name the
 *  side that is actually missing. `showsInFinder` is the US-OR-EU rollup and
 *  cannot distinguish them. */
export function finderMarketEligibility(
  productType: ProductType | null,
  map: Record<string, string>,
): { us: boolean; eu: boolean } {
  if (productType !== 'device') return { us: false, eu: false };
  return {
    us: FINDER_US_PATHWAYS.includes(map['US'] ?? ''),
    eu: FINDER_EU_PATHWAYS.includes(map['EU'] ?? ''),
  };
}

/** Human-readable qualifying classifications per market, for error and hint
 *  copy. Derived from the pathway lists so it cannot drift from the gate — the
 *  EU list gained IVDR C/D when the benchmark finder landed. */
export const FINDER_QUALIFYING_LABEL: Readonly<Record<string, string>> = {
  US: 'a Traditional or Abbreviated 510(k) classification',
  EU: 'MDR Class IIa, IIb or III, or IVDR Class C or D',
};

/**
 * The drug / biologic finders: which one a product type is for, the US
 * classification that lists it, and how the preview names that classification.
 * Mirrors backend/services/finder_eligibility._DRUG_FINDER_RULES; `label` is the same words
 * as its _DRUG_FINDER_QUALIFYING_LABEL (the drug twin of FINDER_QUALIFYING_LABEL above, which
 * is device wording only); tests/test_drug_finder_eligibility.py holds the two equal.
 */
export const DRUG_FINDER_FOR: Readonly<
  Partial<Record<ProductType, { kind: 'generic' | 'reference'; usPathway: string; label: string }>>
> = {
  drug: { kind: 'generic', usPathway: 'anda', label: 'an ANDA (505(j)) classification' },
  biologic: { kind: 'reference', usPathway: 'biosimilar', label: 'a 351(k) biosimilar classification' },
};

/**
 * Whether a product will show in the Predicate Finder given its Step-2 map — a
 * mirror of the backend clause: a device classified in a finder-qualifying US
 * OR EU pathway. Unclassified and every other pathway are excluded. Used to
 * preview the gating consequence.
 */
export function showsInFinder(
  productType: ProductType | null,
  map: Record<string, string>,
): boolean {
  const drugFinder = productType ? DRUG_FINDER_FOR[productType] : undefined;
  if (drugFinder) return map['US'] === drugFinder.usPathway;
  if (productType !== 'device') return false;
  return (
    FINDER_US_PATHWAYS.includes(map['US'] ?? '') ||
    FINDER_EU_PATHWAYS.includes(map['EU'] ?? '')
  );
}

/**
 * Step 2 is complete when at least one country is picked AND every picked
 * country is classified (each has a classification for at least one of its
 * sub-markets). Gates the upload step (Step 3) in product creation.
 */
export function isPathwaySelectionComplete(map: Record<string, string>): boolean {
  const countries = selectedCountries(map);
  return countries.length > 0 && countries.every((c) => isCountryReady(map, c));
}
